import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import {
  getUrlSearchParameter,
  platformStoreDelete,
  platformStoreSet,
  stripHttpProtocol,
} from '../../../../shared/helpers'
import { checkDeviceId, getUserStation } from '../../actions/contentActions'
import { getActivationStatus } from '../../actions/profileActions'
import { registerUser } from '../../actions/userActions'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import googleAnalytics4 from '../../lib/googleAnalytics'
import Platform from '../../platforms'
import { DEVICE_KEY, getActivationRedirectLink, UID_KEY } from './utils'

export function useActivationCode() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  const timerResets = useRef<NodeJS.Timeout[]>([])

  const stopPolling = () => {
    timerResets.current.forEach((timer) => clearTimeout(timer))
    timerResets.current = []
  }

  useEffect(() => stopPolling, [])

  const pollActivation = () => {
    //polling activation status
    getActivationStatus()
      .then((resp) => {
        //if we have the expected result, clear interval and navigate to home

        if (resp.errorCode === 0) {
          //retrieve UID and store it
          if (resp && resp.device && resp.device.UID) {
            platformStoreSet(UID_KEY, resp.device.UID)

            googleAnalytics4.setUserProperty('signed_in_user', resp.device.UID)
            //we have a UID, store in Redux
            dispatch({
              type: 'UPDATE_DEVICE',
              payload: { UID: resp.device.UID },
            })
            registerUser(resp.device.UID).then(() => {
              getUserStation(resp.device.UID).then((localStationResp) => {
                //receive the response from the API and process
                dispatch({
                  type: 'UPDATE_STATION',
                  payload: localStationResp,
                })
                const localCallSign = localStationResp?.call_sign

                googleAnalytics4.setUserProperty(
                  'station_localization',
                  localCallSign
                )

                //Reset/Nullify Home Content so Home page can refresh when it mounts
                dispatch({
                  type: 'SET_HOME_CONTENT',
                  payload: { collections: null },
                })

                registerUser(resp.device.UID).then(() => {
                  // Stop polling
                  stopPolling()

                  //navigate to target
                  const redirectValue = getUrlSearchParameter(
                    location.search,
                    'redirect'
                  )

                  if (redirectValue) {
                    let link = stripHttpProtocol(redirectValue)
                    link += '?fromDeeplink=true'
                    window.deeplink = ''
                    navigate(link)
                  } else {
                    // workaround, if getUserStation is undefined the content is empty
                    // then it should show the station error message
                    if (!localStationResp) {
                      navigate('/error')
                      return
                    }
                    navigate('/home')
                  }
                })
                Platform.signIn(localCallSign)
              })
            })
          }
        } else if (resp.errorCode === 500005) {
          //polling completed, error code 500005
          //expire code,return user to activation screen
          platformStoreDelete(DEVICE_KEY)
          checkDeviceId()
          //navigate to target
          navigate(getActivationRedirectLink(location.search))
        }
      })
      .catch((err) => {
        // brought to the attention of ops that 500 is not the correct status
        // code for this response
        console.warn(err)
      })
  }

  const navigateHome = () => {
    if (user.pid) {
      navigate('/home')
    }
  }

  return {
    pollActivation,
    timerResets,
    user,
    navigateHome,
  }
}
